import { API_ENDPOINT_ROOTS } from '@/constants';
import { firstHyphenChunk } from '@/utils';

import {
  CLUSTER_CAPABILITIES,
  SITE_SERVICES,
  type ClusterCapabilityId,
  type DeviceServiceName,
  type SiteDeviceServicesAvailability,
} from './availableServices.config.ts';

type SiteServiceRow = (typeof SITE_SERVICES)[keyof typeof SITE_SERVICES];

/** Device-picker rows only (`SITE_SERVICES` entries that define `pickerId`), in catalog order. */
const SITE_SERVICES_WITH_PICKER = (
  Object.values(SITE_SERVICES) as readonly SiteServiceRow[]
).filter(
  (row): row is SiteServiceRow & { readonly pickerId: DeviceServiceName } => 'pickerId' in row
);

const containsEdgeRoot = (pod: string, edgeKey: keyof typeof API_ENDPOINT_ROOTS) =>
  pod.toLowerCase().includes(API_ENDPOINT_ROOTS[edgeKey]);

/** First `-` segment starts with `prefix`, or full name contains the edge URL slug. */
const matchesEdgeChunk = (pod: string, prefix: string, edgeKey: keyof typeof API_ENDPOINT_ROOTS) =>
  firstHyphenChunk(pod.toLowerCase()).startsWith(prefix) || containsEdgeRoot(pod, edgeKey);

/** `[capabilityId, chunkPrefix, edgeApiKey]` for workloads matched the usual way. */
const EDGE_POD_ROWS = [
  [CLUSTER_CAPABILITIES.BARGE_CONTROL, 'bargecontrol', 'bargecontrolApi'],
  [CLUSTER_CAPABILITIES.CAMERA, 'cameracontrol', 'cameracontrolApi'],
  [CLUSTER_CAPABILITIES.FEEDING, 'feeding', 'feedingApi'],
  [CLUSTER_CAPABILITIES.GENERATOR, 'generator', 'generatorApi'],
  [CLUSTER_CAPABILITIES.GIO, 'gio', 'gioApi'],
  [CLUSTER_CAPABILITIES.HYBRID_POWER, 'hybrid', 'hybridPowerApi'],
  [CLUSTER_CAPABILITIES.MORT_HANDLING, 'morthandling', 'morthandlingApi'],
  [CLUSTER_CAPABILITIES.ORBIT_LED, 'orbitled', 'orbitLedApi'],
  [CLUSTER_CAPABILITIES.ORBIT_ONE, 'orbitone', 'orbitoneApi'],
  [CLUSTER_CAPABILITIES.WATERLINKED, 'waterlinked', 'waterlinkedApi'],
] as const satisfies ReadonlyArray<
  readonly [ClusterCapabilityId, string, keyof typeof API_ENDPOINT_ROOTS]
>;

const POD_CAPABILITY_RULES = [
  ...EDGE_POD_ROWS.map(([id, prefix, edgeKey]) => ({
    id,
    when: (pod: string) => matchesEdgeChunk(pod, prefix, edgeKey),
  })),
  /** URL substring only — a `firstChunk` prefix `orbit` would match `orbitone` / `orbitled`. */
  {
    id: CLUSTER_CAPABILITIES.ORBIT_SENSOR,
    when: pod => containsEdgeRoot(pod, 'orbitApi'),
  },
];

export const buildStrictCapabilitySetFromPods = (
  pods: readonly string[]
): ReadonlySet<ClusterCapabilityId> => {
  const out = new Set<ClusterCapabilityId>();
  for (const pod of pods) {
    if (!pod) continue;
    for (const { id, when } of POD_CAPABILITY_RULES) {
      if (when(pod)) out.add(id);
    }
  }
  return out;
};

/** When the services API fails or reports no pods, legacy hooks showed every capability (graceful degradation). */
export const ALL_CLUSTER_CAPABILITIES_SET: ReadonlySet<ClusterCapabilityId> = new Set(
  Object.values(CLUSTER_CAPABILITIES)
);

/**
 * Picker ids for cluster capabilities that have a device picker (`SITE_SERVICES.*.pickerId`).
 * `null` set → still loading (unknown); empty → strict match found nothing; full → degraded “show all”.
 */
export const buildSiteDeviceServicesAvailability = (
  clusterCapabilitySet: ReadonlySet<ClusterCapabilityId> | null
): SiteDeviceServicesAvailability => {
  const onCluster = (id: ClusterCapabilityId) => Boolean(clusterCapabilitySet?.has(id));

  return {
    availableServices: SITE_SERVICES_WITH_PICKER.filter(({ capabilityId }) =>
      onCluster(capabilityId)
    ).map(({ pickerId }) => pickerId),
  };
};
